import React, { useEffect, useState, useMemo } from 'react';
import {
	Box,
	Typography,
	Grid,
	TextField,
	Autocomplete,
	CircularProgress,
	Alert,
	Chip,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { useQuery } from 'react-query';
import { getTimeOffReasons, TimeOffReason } from '../../../../../core/API/scheduler';
import { getShiftRequests } from '../../../../../core/API/shift-requests';
import { ShiftRequest } from '../../../../../types/shift-requests';
import { useWorkplacesContext } from '../../../../workplaces/context/WorkplacesContext';
import { fetchAllShifts, Shift } from '../../../../../core/API/shifts';
import { getOrganizations, getDays } from '../../../../../core/API/organizationSettings';
import {
	getBusinessWeekStartForDate,
	resolveStartOfWeekLabel,
} from '../../../utils/scheduleNavigation';

interface TimeOffFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	formRef?: React.MutableRefObject<any>;
	onPublishedStatusChange?: (isPublished: boolean) => void;
	onCheckingStatusChange?: (isChecking: boolean) => void;
}

const today = moment().startOf('day');
const oneYearFromNow = moment().add(1, 'year').endOf('day');

const validationSchema = Yup.object({
	startDate: Yup.date()
		.required('Start date is required')
		.min(today.toDate(), 'Cannot request time off for past dates')
		.max(oneYearFromNow.toDate(), 'Cannot request time off more than one year in advance'),
	endDate: Yup.date()
		.required('End date is required')
		.min(Yup.ref('startDate'), 'End date must be after or equal to start date')
		.max(oneYearFromNow.toDate(), 'Cannot request time off more than one year in advance'),
	reason: Yup.string().required('Time off reason is required'),
	notes: Yup.string(),
});

const TimeOffForm: React.FC<TimeOffFormProps> = ({
	onSubmit,
	onCancel: _onCancel,
	formRef,
	onPublishedStatusChange,
	onCheckingStatusChange,
}) => {
	const [timeOffReasons, setTimeOffReasons] = useState<TimeOffReason[]>([]);
	const [loading, setLoading] = useState(true);
	const [currentUserTimeOff, setCurrentUserTimeOff] = useState<ShiftRequest[]>([]);
	const [checkingTimeOff, setCheckingTimeOff] = useState(false);
	const [isWeekPublished, setIsWeekPublished] = useState(false);
	const [publishedWeeksList, setPublishedWeeksList] = useState<string[]>([]);
	const [checkingPublishedStatus, setCheckingPublishedStatus] = useState(false);
	const { workplaceId } = useWorkplacesContext();

	// Fetch organization settings to get start_of_week
	const { data: organizationData } = useQuery('organizations', () => getOrganizations(), {
		staleTime: 300000,
	});

	// Fetch days data to convert start_of_week from /api/days/id format
	const { data: daysData } = useQuery(['days'], () => getDays(), {
		staleTime: 300000,
	});

	// Get start_of_week from organization settings
	const startOfWeek = useMemo(
		() => resolveStartOfWeekLabel(organizationData?.data?.[0], daysData),
		[organizationData, daysData]
	);

	useEffect(() => {
		const fetchTimeOffReasons = async () => {
			try {
				const reasons = await getTimeOffReasons();
				setTimeOffReasons(reasons.filter((r) => r.active && !r.deleted));
			} catch (error) {
				console.error('Failed to fetch time off reasons:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchTimeOffReasons();
	}, []);

	// Notify parent component when published status changes
	useEffect(() => {
		if (onPublishedStatusChange) {
			onPublishedStatusChange(isWeekPublished);
		}
	}, [isWeekPublished, onPublishedStatusChange]);
	// Notify parent when checking status changes
	useEffect(() => {
		if (onCheckingStatusChange) {
			onCheckingStatusChange(checkingPublishedStatus);
		}
	}, [checkingPublishedStatus, onCheckingStatusChange]);

	// Helper function to filter shifts by date range (client-side filtering)
	const filterShiftsByDateRange = (
		shifts: Shift[],
		startDate: string,
		endDate: string
	): Shift[] => {
		const start = moment(startDate).startOf('day');
		const end = moment(endDate).endOf('day');

		return shifts.filter((shift) => {
			const shiftStart = moment(shift.startAt);
			const shiftEnd = moment(shift.endAt);

			// Include shift if it overlaps with the requested date range
			return shiftStart.isSameOrBefore(end) && shiftEnd.isSameOrAfter(start);
		});
	};

	// Helper function to get business week start for a given date
	const getBusinessWeekStart = (date: moment.Moment, startOfWeekName: string): moment.Moment =>
		moment(getBusinessWeekStartForDate(date, startOfWeekName)).startOf('day');

	// Helper function to get all business weeks that overlap with the timeoff period
	const getOverlappingBusinessWeeks = (
		startDate: string,
		endDate: string
	): Array<{ start: moment.Moment; end: moment.Moment }> => {
		const weeks: Array<{ start: moment.Moment; end: moment.Moment }> = [];

		// Get the business week start for the start date
		let currentWeekStart = getBusinessWeekStart(moment(startDate), startOfWeek);
		const timeoffEnd = moment(endDate).endOf('day');

		// Generate all business weeks that overlap with the timeoff period
		while (currentWeekStart.isSameOrBefore(timeoffEnd)) {
			const currentWeekEnd = currentWeekStart.clone().add(6, 'days').endOf('day');
			weeks.push({
				start: currentWeekStart.clone(),
				end: currentWeekEnd,
			});
			currentWeekStart = currentWeekStart.clone().add(7, 'days');
		}

		return weeks;
	};

	// Function to check if any business week overlapping with timeoff is published
	const checkIfWeekIsPublished = async (startDate: string, endDate: string) => {
		if (!startDate || !endDate || !workplaceId || !startOfWeek) {
			setIsWeekPublished(false);
			setPublishedWeeksList([]);
			return;
		}

		setCheckingPublishedStatus(true);
		try {
			// Get all business weeks that overlap with the timeoff period
			const overlappingWeeks = getOverlappingBusinessWeeks(startDate, endDate);

			// Fetch all shifts (API doesn't filter properly, so we'll filter client-side)
			const response = await fetchAllShifts({
				workplaceId,
				fromDate: overlappingWeeks[0].start.format('YYYY-MM-DD'),
				toDate: overlappingWeeks[overlappingWeeks.length - 1].end.format('YYYY-MM-DD'),
			});

			const allShifts: Shift[] = response.member || response['hydra:member'] || [];

			// Check each business week to see if it's published
			const publishedWeeks: string[] = [];
			let anyWeekPublished = false;

			for (const week of overlappingWeeks) {
				// Filter shifts for this specific business week
				const weekShifts = filterShiftsByDateRange(
					allShifts,
					week.start.format('YYYY-MM-DD'),
					week.end.format('YYYY-MM-DD')
				);

				// If there are shifts in this week and ALL are published, mark the week as published
				if (weekShifts.length > 0) {
					const allWeekShiftsPublished = weekShifts.every((shift) => shift.published === true);
					if (allWeekShiftsPublished) {
						anyWeekPublished = true;
						publishedWeeks.push(
							`${week.start.format('MMM D')} - ${week.end.format('MMM D, YYYY')}`
						);
					}
					console.log(
						`Business week ${week.start.format('MMM D')} - ${week.end.format('MMM D')}: ${weekShifts.length} shifts, all published: ${allWeekShiftsPublished}`
					);
				}
			}

			setIsWeekPublished(anyWeekPublished);
			setPublishedWeeksList(publishedWeeks);
			console.log(
				`Timeoff validation: checked ${overlappingWeeks.length} business weeks, ${publishedWeeks.length} are published`,
				publishedWeeks
			);
		} catch (error) {
			console.error('Failed to check published status:', error);
			setIsWeekPublished(false);
			setPublishedWeeksList([]);
		} finally {
			setCheckingPublishedStatus(false);
		}
	};

	// Function to check for current user's existing time off
	const checkForUsersOnTimeOff = async (startDate: string, endDate: string) => {
		if (!startDate || !endDate || !workplaceId) {
			setCurrentUserTimeOff([]);
			return;
		}

		setCheckingTimeOff(true);
		try {
			// Fetch all time-off requests for the workplace
			const response = await getShiftRequests({
				workplace_id: workplaceId,
			});

			const allRequests = response.member || response['hydra:member'] || [];

			// Filter for time-off requests that overlap with the selected dates
			const selectedStart = moment(startDate);
			const selectedEnd = moment(endDate);

			// Get current user ID from localStorage
			const currentUserId = Number(localStorage.getItem('userId'));

			const currentUserRequests: ShiftRequest[] = [];

			allRequests.forEach((request) => {
				// Check if it's a time-off request
				const isTimeOff =
					request.type?.label?.toLowerCase().includes('time off') ||
					request.type?.label?.toLowerCase().includes('time-off');

				if (!isTimeOff || !request.startAt || !request.endAt) return;

				// Check if approved or pending (not denied)
				const isApprovedOrPending = request.employeeRequestConfirmed !== false;

				if (!isApprovedOrPending) return;

				// Check for date overlap
				const requestStart = moment(request.startAt);
				const requestEnd = moment(request.endAt);

				const hasOverlap =
					requestStart.isSameOrBefore(selectedEnd) && requestEnd.isSameOrAfter(selectedStart);

				if (!hasOverlap) return;

				// Only track current user's requests
				if (currentUserId && request.employeeRequest?.id === currentUserId) {
					currentUserRequests.push(request);
				}
			});

			setCurrentUserTimeOff(currentUserRequests);
		} catch (error) {
			console.error('Failed to fetch time off requests:', error);
			setCurrentUserTimeOff([]);
		} finally {
			setCheckingTimeOff(false);
		}
	};

	const initialValues = {
		startDate: '',
		endDate: '',
		reason: '',
		notes: '',
		reasonDescription: '',
		reasonNote: '',
	};

	const handleFormSubmit = async (formValues: typeof initialValues) => {
		// Re-validate published status before submitting (in case state is stale)
		if (formValues.startDate && formValues.endDate && workplaceId && startOfWeek) {
			try {
				// Get all business weeks that overlap with the timeoff period
				const overlappingWeeks = getOverlappingBusinessWeeks(
					formValues.startDate,
					formValues.endDate
				);

				// Fetch all shifts
				const response = await fetchAllShifts({
					workplaceId,
					fromDate: overlappingWeeks[0].start.format('YYYY-MM-DD'),
					toDate: overlappingWeeks[overlappingWeeks.length - 1].end.format('YYYY-MM-DD'),
				});

				const allShifts: Shift[] = response.member || response['hydra:member'] || [];

				// Check if any business week is published
				for (const week of overlappingWeeks) {
					const weekShifts = filterShiftsByDateRange(
						allShifts,
						week.start.format('YYYY-MM-DD'),
						week.end.format('YYYY-MM-DD')
					);

					if (weekShifts.length > 0 && weekShifts.every((shift) => shift.published === true)) {
						const weekRange = `${week.start.format('MMM D')} - ${week.end.format('MMM D, YYYY')}`;
						alert(
							`Cannot submit time off request: The schedule for business week ${weekRange} has already been published.\n\nTime off requests can only be made for future (unpublished) schedules.\n\nTo request time off for a published week, please apply for a Cover Request instead, or contact your manager.`
						);
						return;
					}
				}
			} catch (error) {
				console.error('Error checking published status during submit:', error);
				// If we can't verify, still block if the state says it's published
				if (isWeekPublished) {
					alert(
						'Cannot submit time off request: One or more business weeks in the selected period have already been published.\n\nPlease apply for a Cover Request instead, or contact your manager.'
					);
					return;
				}
			}
		}

		// Additional state check as fallback
		if (isWeekPublished) {
			const weeksList =
				publishedWeeksList.length > 0
					? `\n\nPublished weeks: ${publishedWeeksList.join(', ')}`
					: '';
			alert(
				`Cannot submit time off request: The schedule for the selected period has already been published.${weeksList}\n\nTime off requests can only be made for future (unpublished) schedules.\n\nTo request time off for a published week, please apply for a Cover Request instead, or contact your manager.`
			);
			return;
		}

		// Convert dates to date-only strings (YYYY-MM-DD format, without time)
		const startDateTime = formValues.startDate
			? moment(formValues.startDate).format('YYYY-MM-DD')
			: null;
		const endDateTime = formValues.endDate ? moment(formValues.endDate).format('YYYY-MM-DD') : null;

		const { reason, reasonDescription, reasonNote, ...rest } = formValues;
		await onSubmit({
			...rest,
			timeOffReasons: reason,
			reasonDescription,
			reasonNote,
			startDateTime,
			endDateTime,
		});
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleFormSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" gutterBottom>
							Request Time Off{' '}
							<Typography component="span" variant="body2" color="text.secondary">
								- For future (unpublished) schedules
							</Typography>
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									type="date"
									label="Start Date"
									value={values.startDate}
									onChange={(e) => {
										const newStartDate = e.target.value;
										setFieldValue('startDate', newStartDate);
										setFieldValue('endDate', newStartDate);
										checkIfWeekIsPublished(newStartDate, newStartDate);
										checkForUsersOnTimeOff(newStartDate, newStartDate);
									}}
									InputLabelProps={{ shrink: true }}
									inputProps={{
										min: moment().format('YYYY-MM-DD'),
										max: moment().add(1, 'year').format('YYYY-MM-DD'),
									}}
									error={touched.startDate && !!errors.startDate}
									helperText={touched.startDate && errors.startDate}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									type="date"
									label="End Date"
									value={values.endDate}
									onChange={(e) => {
										const newEndDate = e.target.value;
										setFieldValue('endDate', newEndDate);
										checkIfWeekIsPublished(values.startDate, newEndDate);
										checkForUsersOnTimeOff(values.startDate, newEndDate);
									}}
									InputLabelProps={{ shrink: true }}
									inputProps={{
										min: values.startDate || moment().format('YYYY-MM-DD'),
										max: moment().add(1, 'year').format('YYYY-MM-DD'),
									}}
									error={touched.endDate && !!errors.endDate}
									helperText={touched.endDate && errors.endDate}
								/>
							</Grid>
							{values.startDate && values.endDate && (
								<Grid item xs={12}>
									{checkingTimeOff || checkingPublishedStatus ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<CircularProgress size={16} />
											<Typography variant="body2" color="text.secondary">
												{checkingPublishedStatus
													? 'Checking schedule status...'
												: 'Checking your existing time off...'}
											</Typography>
										</Box>
									) : (
										<>
											{isWeekPublished && (
												<Alert severity="error" sx={{ py: 0.5, mb: 2 }}>
													<Typography variant="body2" sx={{ fontWeight: 600 }}>
														Schedule already published for selected period
													</Typography>
													<Typography variant="body2" sx={{ mt: 0.5 }}>
														One or more business weeks in your selected timeoff period have already
														been published. Time off requests can only be made for future
														(unpublished) schedules.
													</Typography>
													{publishedWeeksList.length > 0 && (
														<Box sx={{ mt: 1 }}>
															<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
																Published business weeks:
															</Typography>
															{publishedWeeksList.map((week, index) => (
																<Typography key={index} variant="body2" sx={{ ml: 1 }}>
																	• {week}
																</Typography>
															))}
														</Box>
													)}
													<Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
														💡 Please change your timeoff dates or apply for a{' '}
														<strong>Cover Request</strong> instead.
													</Typography>
												</Alert>
											)}
											{currentUserTimeOff.length > 0 && (
												<Alert severity="warning" sx={{ py: 0.5, mb: 2 }}>
													<Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
														You already have time off for this period:
													</Typography>
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
														{currentUserTimeOff.map((request) => (
															<Chip
																key={request.id}
																label={`${moment(request.startAt).format('MMM D, YYYY')} - ${moment(request.endAt).format('MMM D, YYYY')} (${request.employeeRequestConfirmed === true ? 'Approved' : 'Pending'})`}
																size="small"
																color="warning"
																variant="filled"
															/>
														))}
													</Box>
												</Alert>
											)}

										</>
									)}
								</Grid>
							)}
							<Grid item xs={12}>
								<Autocomplete
									options={timeOffReasons}
									getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
									value={timeOffReasons.find((r) => r['@id'] === values.reason) || null}
									onChange={(_, value) => {
										setFieldValue('reason', value ? value['@id'] : '');
										setFieldValue('reasonDescription', value?.description || '');
										setFieldValue('reasonNote', value?.note || '');
									}}
									isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
									loading={loading}
									disabled={loading}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Reason"
											required
											error={touched.reason && Boolean(errors.reason)}
											helperText={touched.reason && (errors.reason as string)}
											margin="normal"
											fullWidth
										/>
									)}
								/>
								{values.reason && (
									<>
										{values.reasonDescription && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={values.reasonDescription}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Description"
											/>
										)}
										{values.reasonNote && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={values.reasonNote}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Note"
											/>
										)}
									</>
								)}
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={3}
									label="Notes (Optional)"
									value={values.notes}
									onChange={(e) => setFieldValue('notes', e.target.value)}
								/>
							</Grid>
						</Grid>
					</Box>
				</Form>
			)}
		</Formik>
	);
};

export default TimeOffForm;
